// Nội dung Trình chiếu của dự án (docs/TRINH-CHIEU-DU-AN.md): tổng quan, vị trí, tiện ích, phối cảnh tòa, mặt bằng tầng / căn,
// ảnh bóc mái 3D, sơ đồ vị trí căn (keyplan), phiếu tính giá & tiến độ thanh toán theo phương án, vay, hỏi đáp, tiến độ, tin tức.
// Lưu riêng khỏi ProjectKit (cột `showroom`) để bảng hàng Chốt căn không phải tải ảnh / đa giác. Toạ độ ô / đa giác tính bằng %
// kích thước ảnh (0–100); toạ độ phối cảnh tòa tính bằng điểm ảnh của ảnh gốc (w × h). Ảnh: link http(s) hoặc /api/files/<id>.
import { z } from 'zod';

const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' }).regex(/^[^\u0000]*$/, 'Chứa ký tự không hợp lệ');
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const reqText = (max: number, message: string) => str(message).trim().min(1, message).max(max, `Tối đa ${max} ký tự`);
const optText = (max = 200) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), text(max).nullish());
const num = () => z.number({ required_error: REQUIRED, invalid_type_error: 'Phải là số' }).finite('Phải là số');
const int = () => num().int('Phải là số nguyên');
const money = num().int('Số tiền phải là số nguyên').min(0, 'Số tiền không được âm').max(9e15, 'Số tiền vượt giới hạn');
const pct = num().min(0, 'Phần trăm không được âm').max(100, 'Phần trăm tối đa 100');
const isoDate = str().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày theo dạng YYYY-MM-DD');
const webUrl = str()
  .trim()
  .max(1000, 'Đường dẫn tối đa 1.000 ký tự')
  .regex(/^https?:\/\/\S+$/i, 'Đường dẫn phải bắt đầu bằng http:// hoặc https://');
/** Ảnh / tệp: link ngoài http(s) hoặc tệp đã tải lên kho tệp của CatHouse (/api/files/<id>). */
export const SHOWROOM_FILE_URL = /^\/api\/files\/[0-9a-f-]{36}$/i;
const mediaUrl = str()
  .trim()
  .max(1000, 'Đường dẫn tối đa 1.000 ký tự')
  .refine((u) => /^https?:\/\/\S+$/i.test(u) || SHOWROOM_FILE_URL.test(u), 'Ảnh phải là link http(s) hoặc tệp đã tải lên (/api/files/…)');
const block = reqText(40, 'Nhập tên toà');
const axis = reqText(12, 'Nhập trục căn');
const floor = int().min(-10, 'Tầng không hợp lệ').max(500, 'Tầng không hợp lệ');
const px = int().min(1, 'Kích thước ảnh không hợp lệ').max(20_000, 'Ảnh quá lớn');
/** Toạ độ % trên ảnh (0–100). */
const coord = num().min(0, 'Toạ độ 0–100').max(100, 'Toạ độ 0–100');
export const showroomPoint = z.tuple([coord, coord]);
export const showroomPolygon = z.array(showroomPoint).min(3, 'Vùng chọn cần ít nhất 3 điểm').max(80, 'Vùng chọn tối đa 80 điểm');
/** Điểm ảnh trên ảnh gốc (phối cảnh tòa). */
const pxPoint = z.tuple([num().min(0).max(20_000), num().min(0).max(20_000)]);
const axisCells = z
  .record(str().trim().min(1).max(12), showroomPolygon)
  .refine((r) => Object.keys(r).length <= 120, 'Tối đa 120 trục mỗi mặt bằng');

export const SHOWROOM_LIMITS = {
  facts: 24,
  highlights: 20,
  gallery: 80,
  connections: 12,
  amenities: 80,
  towers: 100,
  floorPlans: 300,
  unitPlans: 400,
  roofOff: 400,
  keyplans: 100,
  orient: 800,
  grossAreas: 20_000,
  schemes: 8,
  steps: 30,
  terms: 30,
  faq: 100,
  milestones: 60,
  news: 60,
  tours: 20,
  contacts: 10,
  /** JSON đã chuẩn hoá (byte UTF-8): vừa một thông điệp RPC (NATS mặc định 1 MB). */
  bytes: 750_000,
} as const;
const list = <T extends z.ZodTypeAny>(item: T, max: number, label: string) => z.array(item).max(max, `Tối đa ${max} ${label}`).default([]);

export const GALLERY_KINDS = ['render', 'amenity', 'interior', 'masterplan', 'location', 'progress', 'other'] as const;
export type GalleryKind = (typeof GALLERY_KINDS)[number];
export const GALLERY_KIND_LABEL: Record<GalleryKind, string> = {
  render: 'Phối cảnh',
  amenity: 'Tiện ích',
  interior: 'Nội thất',
  masterplan: 'Mặt bằng tổng thể',
  location: 'Vị trí',
  progress: 'Tiến độ',
  other: 'Khác',
};
/** Loại dòng tiến độ: deposit = tiền đặt trước; bank = ngân hàng giải ngân (không tính vào phần khách tự trả);
 *  ttdc = vào Thỏa thuận đặt cọc; offset = khấu trừ (không cộng vào tổng); own = khách thanh toán. */
export const PAY_STEP_KINDS = ['deposit', 'own', 'bank', 'ttdc', 'offset'] as const;
export type PayStepKind = (typeof PAY_STEP_KINDS)[number];
/** Cơ sở tính tỷ lệ: gross = giá bán gồm VAT (chưa KPBT), net = giá bán thuần chưa VAT, total = gồm VAT & KPBT. */
export const PAY_STEP_BASES = ['gross', 'net', 'total'] as const;
export type PayStepBase = (typeof PAY_STEP_BASES)[number];

const factInput = z.object({ label: reqText(60, 'Nhập tên thông số'), value: reqText(300, 'Nhập giá trị') });
const galleryInput = z.object({ url: mediaUrl, caption: optText(160), kind: z.enum(GALLERY_KINDS).default('render') });
const connectionInput = z.object({ time: reqText(40, 'Nhập thời gian di chuyển'), places: z.array(reqText(160, 'Nhập địa điểm')).max(20, 'Tối đa 20 địa điểm').default([]) });
const locationInput = z.object({
  address: optText(300),
  /** Link Google Maps / bản đồ. */
  mapUrl: webUrl.nullish(),
  image: mediaUrl.nullish(),
  connections: list(connectionInput, SHOWROOM_LIMITS.connections, 'nhóm kết nối'),
});
const amenitiesInput = z.object({
  outdoor: list(reqText(160, 'Nhập tên tiện ích'), SHOWROOM_LIMITS.amenities, 'tiện ích ngoài trời'),
  indoor: list(reqText(160, 'Nhập tên tiện ích'), SHOWROOM_LIMITS.amenities, 'tiện ích trong nhà'),
  masterplan: mediaUrl.nullish(),
});
const towerInput = z.object({
  block,
  /** Tên hiển thị (VD "Tòa L1"); trống ⇒ dùng tên toà. */
  name: optText(60),
  /** Thứ tự trục vật lý trên mặt bằng tầng (bảng hàng dạng lưới, màn tầng). */
  axes: z.array(axis).max(120, 'Tối đa 120 trục').default([]),
  /** Tầng điển hình để so ảnh bóc mái 3D (cùng loại căn, diện tích lệch ≤ 4%). */
  typicalFloor: floor.nullish(),
});
const facadeTowerInput = z.object({
  block,
  /** Mép trái / phải mặt tòa, đáy tầng thấp nhất, đỉnh tầng cao nhất (điểm ảnh). */
  x0: num().min(0).max(20_000),
  x1: num().min(0).max(20_000),
  yBottom: num().min(0).max(20_000),
  yTop: num().min(0).max(20_000),
  /** Dải tầng trên ảnh; trống ⇒ tầng thấp / cao nhất của toà trong bảng hàng. */
  floorFrom: floor.nullish(),
  floorTo: floor.nullish(),
  /** Đường viền tòa trên ảnh để cắt dải tầng (điểm ảnh); trống ⇒ hình chữ nhật x0..x1 × yTop..yBottom. */
  clip: z.array(pxPoint).max(80, 'Đường viền tối đa 80 điểm').nullish(),
  label: pxPoint.nullish(),
});
const facadeInput = z.object({
  image: mediaUrl,
  w: px,
  h: px,
  /** Khung nhìn [x, y, rộng, cao] (điểm ảnh); trống ⇒ cả ảnh. */
  view: z.tuple([num().min(0), num().min(0), num().positive(), num().positive()]).nullish(),
  towers: z.array(facadeTowerInput).min(1, 'Cần ít nhất một tòa').max(SHOWROOM_LIMITS.towers),
});
const floorPlanInput = z.object({
  block,
  /** Nhãn dải tầng của bản vẽ (VD "12-22"). */
  levels: reqText(40, 'Nhập dải tầng'),
  floors: z.array(floor).min(1, 'Cần ít nhất một tầng').max(500),
  image: mediaUrl,
  w: px,
  h: px,
  /** Vùng chọn từng trục trên ảnh mặt bằng tầng. */
  cells: axisCells.default({}),
});
const unitPlanInput = z.object({
  block,
  axes: z.array(axis).min(1, 'Cần ít nhất một trục').max(30),
  /** Loại căn (so khớp với loại căn trong bảng hàng, bỏ khoảng trắng, không phân biệt hoa thường); trống ⇒ mọi loại. */
  type: optText(60),
  image: mediaUrl,
  /** Vị trí chữ "Lối vào" trên ảnh (%), hiện lại bằng nhãn đứng thẳng khi xoay / lật. */
  entry: showroomPoint.nullish(),
});
const roofOffInput = z.object({
  block,
  axis,
  /** Tờ riêng cho các tầng này; trống ⇒ tờ tầng điển hình. */
  floors: z.array(floor).max(500).nullish(),
  image: mediaUrl,
  w: px,
  h: px,
});
const keyplanInput = z.object({ block, image: mediaUrl, w: px, h: px, cells: axisCells.default({}) });
export const PLAN_ROTATIONS = [0, 90, 180, 270] as const;
const orientInput = z.object({
  rot: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)], { errorMap: () => ({ message: 'Góc xoay 0 / 90 / 180 / 270' }) }),
  flip: z.boolean().default(false),
});
const pricingInput = z.object({
  /** Tiền đất không chịu VAT trên mỗi m² thông thủy (phiếu tính giá CĐT); trống ⇒ VAT tính trên cả giá thuần. */
  landPerM2: money.nullish(),
  vatPct: pct.default(10),
  /** Kinh phí bảo trì, % giá bán thuần. */
  kpbtPct: pct.default(2),
  /** Tiền đặt trước khi ký XNĐK; trống ⇒ tiền giữ chỗ của dự án. */
  deposit: money.nullish(),
  policyName: optText(160),
  validFrom: isoDate.nullish(),
  validTo: isoDate.nullish(),
  /** Tên ngày bắt đầu tính tiến độ (VD "Ngày ký XNĐK"). */
  startLabel: optText(60),
  note: optText(1000),
});
const payStepInput = z
  .object({
    label: reqText(60, 'Nhập tên đợt'),
    /** Dòng tiêu đề nhóm phía trên đợt này (đổi nhóm ⇒ hiện tiêu đề). */
    group: optText(120),
    /** Hạn = ngày bắt đầu + số ngày; hoặc ngày cố định; hoặc chỉ có mô tả (theo thông báo bàn giao). */
    days: int().min(0).max(36_500).nullish(),
    date: isoDate.nullish(),
    when: optText(200),
    pct: pct.nullish(),
    base: z.enum(PAY_STEP_BASES).default('gross'),
    /** Trừ tiền đặt trước khỏi đợt này. */
    lessDeposit: z.boolean().default(false),
    /** Cộng kinh phí bảo trì. */
    addKpbt: z.boolean().default(false),
    /** Cộng VAT của X% giá bán (VD 5% giữ lại đến khi cấp GCN). */
    addVatPct: pct.nullish(),
    kind: z.enum(PAY_STEP_KINDS).default('own'),
    /** Hiện thay cho tỷ lệ (VD "KPBT"). */
    pctLabel: optText(20),
    note: optText(200),
  })
  .refine((s) => s.days == null || s.date == null, { message: 'Chọn số ngày hoặc ngày cố định, không cả hai', path: ['date'] });
const termInput = z.object({ label: reqText(60, 'Nhập tên đợt'), when: optText(200), value: reqText(300, 'Nhập nội dung') });
const schemeInput = z.object({
  /** Mã phương án giá của ProjectKit.priceSchemes (giá ở UnitAttrs.prices) hoặc 'list' = giá niêm yết. */
  key: str().regex(/^[a-z][a-z0-9_]{0,23}$/, 'Mã phương án: chữ thường, số, gạch dưới'),
  label: reqText(80, 'Nhập tên phương án'),
  short: optText(16),
  /** Hệ số trên giá bán thuần gốc (TT sớm chiết khấu 13% ⇒ 0,87; vay HTLS +8,8% ⇒ 1,088). */
  factor: num().min(0.3, 'Hệ số 0,3–2').max(2, 'Hệ số 0,3–2').default(1),
  adjustLabel: optText(120),
  summary: optText(400),
  priceNote: optText(200),
  /** Bảng điều khoản như CSBH (đọc cho khách). */
  terms: list(termInput, SHOWROOM_LIMITS.terms, 'dòng điều khoản'),
  steps: list(payStepInput, SHOWROOM_LIMITS.steps, 'đợt thanh toán'),
});
const loanInput = z.object({
  scheme: str().regex(/^[a-z][a-z0-9_]{0,23}$/, 'Mã phương án không hợp lệ'),
  /** Tỷ lệ vay trên giá bán gồm VAT. */
  ratio: pct.default(70),
  bank: optText(80),
  /** CĐT hỗ trợ lãi suất 0% đến ngày này (hoặc bàn giao). */
  zeroUntil: isoDate.nullish(),
  /** Số ngày từ ngày bắt đầu đến lần giải ngân đầu. */
  disburseDays: int().min(0).max(3650).default(37),
  fixedMonths: int().min(0).max(120).default(24),
  fixedRate: pct.default(7.5),
  floatRate: pct.default(10.5),
  maxYears: int().min(1).max(50).default(35),
  note: optText(400),
});
const faqInput = z.object({ q: reqText(300, 'Nhập câu hỏi'), a: reqText(4000, 'Nhập câu trả lời'), group: optText(60) });
const milestoneInput = z.object({ date: reqText(40, 'Nhập mốc thời gian'), title: reqText(160, 'Nhập tiêu đề'), text: optText(1000), image: mediaUrl.nullish(), done: z.boolean().default(false) });
const newsInput = z.object({ date: isoDate.nullish(), title: reqText(200, 'Nhập tiêu đề'), source: optText(80), url: webUrl.nullish(), summary: optText(600) });
const tourInput = z.object({ title: reqText(120, 'Nhập tên'), url: webUrl });
const contactInput = z.object({ name: reqText(80, 'Nhập tên'), role: optText(80), phone: optText(40) });
const grossAreas = z
  .record(str().trim().min(1).max(64), num().positive().max(1_000_000))
  .refine((r) => Object.keys(r).length <= SHOWROOM_LIMITS.grossAreas, `Tối đa ${SHOWROOM_LIMITS.grossAreas} căn`);
const orientMap = z
  .record(str().trim().min(1).max(64), orientInput)
  .refine((r) => Object.keys(r).length <= SHOWROOM_LIMITS.orient, `Tối đa ${SHOWROOM_LIMITS.orient} hướng mặt bằng`);

/** Toàn bộ nội dung Trình chiếu (PUT thay cả bộ). */
export const projectShowroomInput = z.object({
  intro: optText(4000),
  facts: list(factInput, SHOWROOM_LIMITS.facts, 'thông số'),
  highlights: list(reqText(300, 'Nhập điểm nổi bật'), SHOWROOM_LIMITS.highlights, 'điểm nổi bật'),
  gallery: list(galleryInput, SHOWROOM_LIMITS.gallery, 'ảnh'),
  location: locationInput.nullish(),
  amenities: amenitiesInput.nullish(),
  towers: list(towerInput, SHOWROOM_LIMITS.towers, 'tòa'),
  facade: facadeInput.nullish(),
  floorPlans: list(floorPlanInput, SHOWROOM_LIMITS.floorPlans, 'mặt bằng tầng'),
  unitPlans: list(unitPlanInput, SHOWROOM_LIMITS.unitPlans, 'mặt bằng căn'),
  roofOff: list(roofOffInput, SHOWROOM_LIMITS.roofOff, 'ảnh bóc mái'),
  keyplans: list(keyplanInput, SHOWROOM_LIMITS.keyplans, 'sơ đồ vị trí'),
  /** Hướng mặt bằng 2D cho khớp ảnh 3D, khoá "toà|trục" hoặc "toà|trục|t<tầng>" (tờ riêng). */
  planOrient: orientMap.default({}),
  /** Diện tích tim tường theo mã căn (bảng hàng chỉ có thông thủy). */
  grossAreas: grossAreas.default({}),
  pricing: pricingInput.nullish(),
  schemes: list(schemeInput, SHOWROOM_LIMITS.schemes, 'phương án thanh toán').refine((s) => new Set(s.map((x) => x.key)).size === s.length, 'Mã phương án bị trùng'),
  loan: loanInput.nullish(),
  faq: list(faqInput, SHOWROOM_LIMITS.faq, 'câu hỏi'),
  milestones: list(milestoneInput, SHOWROOM_LIMITS.milestones, 'mốc tiến độ'),
  news: list(newsInput, SHOWROOM_LIMITS.news, 'tin'),
  tours: list(tourInput, SHOWROOM_LIMITS.tours, 'ảnh 360°'),
  /** Đầu mối dự án hiện ở trang căn (cùng sale đang trình chiếu). */
  contacts: list(contactInput, SHOWROOM_LIMITS.contacts, 'đầu mối'),
});
export type ProjectShowroom = z.output<typeof projectShowroomInput>;
export type ShowroomTower = ProjectShowroom['towers'][number];
export type ShowroomFacade = NonNullable<ProjectShowroom['facade']>;
export type ShowroomFloorPlan = ProjectShowroom['floorPlans'][number];
export type ShowroomUnitPlan = ProjectShowroom['unitPlans'][number];
export type ShowroomRoofOff = ProjectShowroom['roofOff'][number];
export type ShowroomKeyplan = ProjectShowroom['keyplans'][number];
export type ShowroomPricing = NonNullable<ProjectShowroom['pricing']>;
export type ShowroomScheme = ProjectShowroom['schemes'][number];
export type ShowroomPayStep = ShowroomScheme['steps'][number];
export type ShowroomLoan = NonNullable<ProjectShowroom['loan']>;
export type ShowroomOrient = ProjectShowroom['planOrient'][string];
export type ShowroomPoint = z.output<typeof showroomPoint>;

export const emptyShowroom = (): ProjectShowroom => projectShowroomInput.parse({});
/** Đã có nội dung trình chiếu chưa (trang Trình chiếu hiện hướng dẫn nhập khi rỗng). */
export const hasShowroomContent = (s: ProjectShowroom) =>
  !!(s.intro || s.facts.length || s.gallery.length || s.towers.length || s.facade || s.floorPlans.length || s.unitPlans.length || s.schemes.length);

export const setProjectShowroomInput = z.object({
  projectId: str().uuid('Mã định danh không hợp lệ'),
  /** Phiên bản nội dung trình chiếu (If-Match) — tách khỏi phiên bản dự án để sửa song song không chặn nhau. */
  expectedVersion: int().min(0, 'Phiên bản không hợp lệ'),
  showroom: projectShowroomInput,
});
export const getProjectShowroomInput = z.object({ projectId: str().uuid('Mã định danh không hợp lệ') });

/** Kết quả GetProjectShowroom / SetProjectShowroom. */
export interface ProjectShowroomView {
  projectId: string;
  /** 0 = chưa từng lưu. */
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  showroom: ProjectShowroom;
}
